import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { FolderOpen, Paperclip } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useAttachments } from './attachments-provider';
import { useDragHandlers } from './chat-input-context';
import { useChatInputContext } from './chat-input-context';
import { useFileSelection, type FileAttachment } from '../../../../hooks/use-file-selection';
import type { UIMessageFileMetadata } from '../../../../lib/types';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatAttachmentsProps {
  className?: string;
}

function buildMessageFileMetadata(attachment: FileAttachment): UIMessageFileMetadata {
  if (attachment.kind === 'folder' && attachment.folderMetadata) {
    return {
      uuid: attachment.fileUuid!,
      filename: attachment.file.name,
      kind: 'folder',
      folderName: attachment.folderMetadata.folderName,
      fileCount: attachment.folderMetadata.fileCount,
      totalBytes: attachment.folderMetadata.totalBytes,
      skippedFileCount: attachment.folderMetadata.skippedFileCount,
      archiveFilename: attachment.folderMetadata.archiveFilename,
      truncated: attachment.folderMetadata.truncated,
    };
  }

  return {
    uuid: attachment.fileUuid!,
    filename: attachment.file.name,
  };
}

/**
 * Component for managing file attachments in chat input.
 * Manages its own attachment state and file selection logic.
 * Contributes attachment data to additionalContext via ChatOverlayContext.
 *
 * This component is decoupled from the input setup - it simply sets additionalContext
 * which is then used when the message is sent.
 *
 * Includes only the attachment button and file input.
 * Note: The attachments preview is rendered separately.
 */
export function ChatAttachments({ className }: ChatAttachmentsProps) {
  // Manage attachments state locally
  const { attachments, setAttachments } = useAttachments();
  const { chatUuid, setAdditionalContext } = useChatOverlayContext();
  const { setHandlers } = useDragHandlers();
  const { status } = useChatInputContext();
  const isLocalFolderAttachmentsEnabled = useFeatureFlag('nova-local-folder-attachments', false);

  // Use file selection hook with local state
  const {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    handleFolderAttachmentClick,
    supportedFileTypes,
  } = useFileSelection({ setAttachments, chatUuid });

  // Track previous status to detect when message is sent
  const prevStatusRef = useRef(status);

  // Clear attachments when message is sent
  useEffect(() => {
    if (prevStatusRef.current !== 'submitted' && status === 'submitted') {
      // Message was just sent, clear attachments
      setAttachments([]);
      setAdditionalContext((prev) => {
        const { attachmentFileUuids, messageMetadata, attachments: _, ...rest } = prev;
        return rest;
      });
    }
    prevStatusRef.current = status;
  }, [status, setAttachments, setAdditionalContext]);

  // Update drag handlers ref whenever they change
  useEffect(() => {
    setHandlers({
      isDragging,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handlePaste,
    });
  }, [isDragging, handleDragOver, handleDragLeave, handleDrop, handlePaste, setHandlers]);

  // Update additionalContext with attachment data whenever it changes
  useEffect(() => {
    const successfulAttachments = attachments.filter((attachment) => attachment.fileUuid && !attachment.uploadError);
    const attachmentFileUuids = successfulAttachments.map((attachment) => attachment.fileUuid!);
    const fileMetadata = successfulAttachments.map(buildMessageFileMetadata);

    setAdditionalContext((prev) => ({
      ...prev,
      attachmentFileUuids,
      // Set messageMetadata that will be attached to the message when sent
      // Components can contribute metadata by setting messageMetadata in additionalContext
      messageMetadata: fileMetadata.length > 0 ? { files: fileMetadata } : undefined,
    }));
  }, [attachments, setAdditionalContext]);

  return (
    <div className={cn('flex items-center', className)}>
      <Button
        data-testid="attachment-button"
        variant="transparent"
        className={cn(
          'text-muted-foreground h-fit rounded-full bg-transparent p-2 transition-colors',
          'hover:text-foreground border-none',
        )}
        onClick={handleAttachmentClick}
        type="button"
      >
        <Paperclip size={16} />
      </Button>

      {isLocalFolderAttachmentsEnabled ? (
        <Button
          aria-label="Attach folder"
          data-testid="folder-attachment-button"
          title="Attach folder"
          variant="transparent"
          className={cn(
            'text-muted-foreground h-fit rounded-full bg-transparent p-2 transition-colors',
            'hover:text-foreground border-none',
          )}
          onClick={() => void handleFolderAttachmentClick()}
          type="button"
        >
          <FolderOpen size={16} />
        </Button>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept={supportedFileTypes.join(',')}
        multiple
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
}
